import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import * as Sentry from '@sentry/react'

/** Request body for POST /fhir/patient/search (Epic R4 Patient?given&family&birthdate). */
export interface PatientFhirSearchRequest {
  accessToken: string
  organizationId: string
  given: string
  family: string
  /** ISO calendar date YYYY-MM-DD */
  birthdate: string
}

/** One telecom entry from Epic Patient (FHIR shape). */
export interface PatientTelecomItem {
  system?: string | null
  value?: string | null
  use?: string | null
}

/** One Patient match from POST /fhir/patient/search. */
export interface PatientSearchItem {
  fhirId?: string | null
  mrn?: string | null
  telecom: PatientTelecomItem[]
}

function normalizePatientSearchItem(o: Record<string, unknown>): PatientSearchItem {
  const telecomRaw = o.telecom
  const telecom = Array.isArray(telecomRaw) ? (telecomRaw as PatientTelecomItem[]) : []
  const fhirId = o.fhirId
  const mrn = o.mrn
  return {
    fhirId: typeof fhirId === 'string' ? fhirId : fhirId === null ? null : undefined,
    mrn: typeof mrn === 'string' ? mrn : mrn === null ? null : undefined,
    telecom,
  }
}

/**
 * Search Epic FHIR R4 Patient by given, family, and birthdate via provider-api.
 * Requires Epic launch context (epicState) for accessToken and organizationId.
 *
 * @returns Matches on success (possibly empty), or `null` if the session is missing or the request failed.
 */
export async function searchPatientsByDemographics(
  epicState: string,
  params: { given: string; family: string; birthdate: string },
  signal?: AbortSignal
): Promise<PatientSearchItem[] | null> {
  const epic = EpicAuthClient.getInstance()
  if (!epic.hasSession(epicState)) {
    return null
  }
  const accessToken = epic.getAccessToken(epicState)
  const organizationId = epic.getOrgId(epicState)
  if (!accessToken || !organizationId) {
    return null
  }

  const body: PatientFhirSearchRequest = {
    accessToken,
    organizationId,
    given: params.given.trim(),
    family: params.family.trim(),
    birthdate: params.birthdate.trim(),
  }

  const providerApi = ProviderAPIClient.getInstance()
  let response: Response
  try {
    response = await providerApi.post(
      '/fhir/patient/search',
      body,
      { 'Content-Type': 'application/json' },
      true,
      signal
    )
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err
    }
    Sentry.captureException(err instanceof Error ? err : new Error('Patient FHIR search failed'), {
      tags: { operation: 'fhir.patient.search' },
    })
    return null
  }

  if (!response.ok) {
    Sentry.captureException(new Error(`Patient FHIR search failed: ${response.status}`), {
      tags: { operation: 'fhir.patient.search', status: String(response.status) },
    })
    return null
  }

  let json: unknown
  try {
    json = await response.json()
  } catch {
    return null
  }

  if (!Array.isArray(json)) {
    return null
  }

  return json
    .filter((x): x is Record<string, unknown> => x !== null && typeof x === 'object')
    .map((row) => normalizePatientSearchItem(row))
}
